import { QueryClientProvider } from '@tanstack/react-query';
import { useMemo } from 'react';
import { AuthGate, SignedOutPage, useAuth } from './auth/AuthGate';
import { MondayCallbackPage } from './components/MondayStates';
import { RoadmapApp } from './components/RoadmapApp';
import { ScreenMessage } from './components/ui/ScreenMessage';
import { Toaster } from './components/ui/Toaster';
import { isApiError } from './lib/api';
import { createQueryClient, useMe } from './lib/queries';

/** Client routes: /  /auth/callback (handled by AuthGate)  /signed-out  /monday/callback. */
export function App() {
  const path = window.location.pathname;
  if (path === '/signed-out') return <SignedOutPage />;
  return (
    <AuthGate>
      <Authenticated />
    </AuthGate>
  );
}

function Authenticated() {
  // read after AuthGate finished: /auth/callback has been replaced by the return path by then
  const path = window.location.pathname;
  const client = useMemo(() => createQueryClient(), []);
  return (
    <QueryClientProvider client={client}>
      {path === '/monday/callback' ? <MondayCallbackPage /> : <Gate />}
      <Toaster />
    </QueryClientProvider>
  );
}

/** Resolves the current user; unauthorised users get a clear screen instead of a broken app. */
function Gate() {
  const me = useMe();
  const auth = useAuth();
  if (me.isLoading) return <ScreenMessage title="Loading Mona Roadmap" busy />;
  if (me.error) {
    if (isApiError(me.error) && me.error.code === 'FORBIDDEN') {
      return (
        <ScreenMessage
          title="You are not authorized"
          tone="error"
          detail="Your account is signed in but does not have access to Mona Roadmap. Ask an administrator to grant you access."
          action={{ label: 'Sign out', onClick: auth.signOut }}
        />
      );
    }
    return <ScreenMessage title="Could not load your profile" tone="error" detail={me.error.message} action={{ label: 'Retry', onClick: () => void me.refetch() }} secondary={{ label: 'Sign out', onClick: auth.signOut }} />;
  }
  return <RoadmapApp me={me.data!} />;
}
